import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  AlertTriangle,
  TrendingUp,
  Trash2,
  ClipboardCheck,
  Snowflake,
  ArrowRight,
  Activity,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import StatCard from '../../components/dashboard/StatCard';
import DemoBanner from '../../components/optimar/DemoBanner';
import SectionBreak from '../../components/optimar/SectionBreak';
import ProductionStagesTimeline from '../../components/optimar/ProductionStagesTimeline';
import WarningBanner from '../../components/optimar/WarningBanner';
import ResolveWarningDialog from '../../components/optimar/ResolveWarningDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OptimarDashboard = ({ user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeWarning, setActiveWarning] = useState(null);

  const load = useCallback(async () => {
    try {
      const [dashRes, stageRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/optimar/dashboard`),
        axios.get(`${BACKEND_URL}/api/optimar/batches/OPT-DEMO-001/stages`),
      ]);
      setDashboard(dashRes.data);
      setStages(stageRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load Optimar demo dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openResolveDialog = (warning) => {
    setActiveWarning(warning);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center" data-testid="optimar-dashboard-loading">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-slate-600">Loading Optimar demo…</p>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;
  const { batch, cards, warnings, last_updated: lastUpdated } = dashboard;

  return (
    <div className="space-y-10 sm:space-y-12" data-testid="optimar-dashboard-page">
      <DemoBanner />

      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Aqua Processing Line Demo
          </p>
          <h1 className="mt-1 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl lg:text-4xl" style={{ fontFamily: 'Manrope' }}>
            {batch.product_name}
          </h1>
          <p className="text-base italic text-slate-600 sm:text-lg">
            {batch.species} · Batch{' '}
            <span className="font-mono not-italic">{batch.batch_id}</span>
          </p>
          <p
            className="mt-2 text-xs text-slate-500"
            data-testid="optimar-dashboard-platform-attribution"
          >
            The <span className="font-semibold text-slate-700">PureSustain Platform</span>{' '}
            is a product of{' '}
            <span className="font-semibold text-slate-700">PureSustain AS</span>, under{' '}
            <span className="font-semibold text-slate-700">VeraBlue Capital AS</span>.
          </p>
        </div>
        <div className="text-sm text-slate-500">
          Status: <span className="font-medium text-slate-800">{batch.current_status}</span>
          <br />Last updated: {new Date(lastUpdated).toLocaleString('en-NO')}
        </div>
      </div>

      <div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
        data-testid="optimar-dashboard-cards"
      >
        <StatCard
          icon={ShieldCheck}
          label="Traceability confidence"
          value={`${cards.traceability_confidence}%`}
          trend="Across all logged production steps"
          iconColor="text-emerald-700"
          bgColor="bg-emerald-100"
        />
        <StatCard
          icon={AlertTriangle}
          label="Active alerts"
          value={cards.active_alerts}
          trend="Open traceability warnings"
          iconColor="text-amber-700"
          bgColor="bg-amber-100"
        />
        <StatCard
          icon={TrendingUp}
          label="Yield percentage"
          value={`${cards.yield_percentage}%`}
          trend={`Processed ${batch.processed_weight_kg.toLocaleString()} kg of ${batch.raw_weight_kg.toLocaleString()} kg`}
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
        <StatCard
          icon={Trash2}
          label="Discarded product weight"
          value={`${cards.discarded_weight_kg.toLocaleString()} kg`}
          trend="Logged in plant waste table (demo)"
          iconColor="text-rose-700"
          bgColor="bg-rose-100"
        />
        <StatCard
          icon={ClipboardCheck}
          label="Compliance status"
          value={cards.compliance_status}
          trend="HACCP-style temperature control + audit history"
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
        <StatCard
          icon={Snowflake}
          label="Current storage location"
          value={cards.current_storage_location}
          trend={`Avg temperature ${batch.average_temperature_celsius} °C`}
          iconColor="text-sky-700"
          bgColor="bg-sky-100"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-blue-50 p-2">
            <FileText className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              Demo compliance report preview
            </p>
            <p className="text-xs text-slate-500">
              Aggregated identity, traceability, HACCP, manual corrections and
              customer summary for this batch.
            </p>
          </div>
        </div>
        <Link
          to="/optimar/report"
          className="inline-flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100"
          data-testid="optimar-dashboard-view-report"
        >
          <FileText className="h-4 w-4" /> View compliance report
        </Link>
      </div>

      {warnings.length > 0 && (
        <>
          <SectionBreak data-testid="optimar-dashboard-break-before-warning" />
          <WarningBanner
            warning={warnings[0]}
            onResolveClick={() => openResolveDialog(warnings[0])}
            testIdPrefix="optimar-warning"
          />
        </>
      )}

      <ResolveWarningDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        warning={activeWarning}
        user={user}
        onResolved={() => load()}
      />

      <SectionBreak data-testid="optimar-dashboard-break-before-stages" />
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 lg:p-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-lg bg-slate-100 p-2 shrink-0">
              <Activity className="h-5 w-5 text-slate-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 sm:text-xl" style={{ fontFamily: 'Manrope' }}>
                Production stages
              </h2>
              <p className="text-sm text-slate-500">
                11 stages on the Aqua Processing Line Demo
              </p>
            </div>
          </div>
          <Link
            to="/optimar/batch"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
            data-testid="optimar-stages-link"
          >
            Full batch view <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <ProductionStagesTimeline stages={stages} />
      </div>
    </div>
  );
};

export default OptimarDashboard;
